import { parseEuroToCents } from '../../../shared/utils/installments';
import { getDealInstallmentCents, getDealPercentage } from '../../../shared/utils/dealRevenue';
import { entitySelectionPayload } from '../../../shared/utils/entitySelection';
import * as mock from '@/modules/deals/mocks/deals.mock';
import { getStoredSessionToken } from '../../../app/services/session.service';
import {
  idsMatch,
  validateAssignmentForm,
} from '@/modules/deals/utils';

function buildMutationResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

const DEALS_API_BASE_URL = (process.env.REACT_APP_DEALS_API_BASE_URL || '').trim();
const ACCOUNTING_API_BASE_URL = (process.env.REACT_APP_ACCOUNTING_API_BASE_URL || '').trim();
const ASSIGNMENTS_API_PATH = '/api/deals/assignments';
const PROPOSALS_API_PATH = '/api/deals/proposals';
const IN_PROGRESS_API_PATH = '/api/deals/in-progress';
const COMPLETED_API_PATH = '/api/deals/completed';
const CUSTOM_FIELDS_API_PATH = '/api/deals/custom-fields';
const ACCOUNTING_COMPLETED_DEALS_API_PATH = '/api/accounting/completed-deals';
const IN_PROGRESS_PROMOTED_EVENT = 'deals:in-progress-promoted';
const COMPLETED_DEAL_CREATED_EVENT = 'deals:completed-created';
const TENANT_DB_ERROR_CODES = new Set([
  'tenant_database_not_ready',
  'tenant_database_unavailable',
  'tenant_database_metadata_missing',
]);

function getAssignmentsApiUrl(path = '') {
  return `${DEALS_API_BASE_URL}${ASSIGNMENTS_API_PATH}${path}`;
}

function getProposalsApiUrl(path = '') {
  return `${DEALS_API_BASE_URL}${PROPOSALS_API_PATH}${path}`;
}

function getInProgressApiUrl(path = '') {
  return `${DEALS_API_BASE_URL}${IN_PROGRESS_API_PATH}${path}`;
}

function getCompletedApiUrl(path = '') {
  return `${DEALS_API_BASE_URL}${COMPLETED_API_PATH}${path}`;
}

function getDealCustomFieldsApiUrl(formKey, path = '') {
  return `${DEALS_API_BASE_URL}${CUSTOM_FIELDS_API_PATH}/${encodeURIComponent(formKey)}${path}`;
}

function getDealCommentsApiUrl(dealType, dealId) {
  return `${DEALS_API_BASE_URL}/api/deals/${dealType}/${dealId}/comments`;
}

function getCompletedDealSettlementApiUrl(completedDealId) {
  return `${ACCOUNTING_API_BASE_URL}${ACCOUNTING_COMPLETED_DEALS_API_PATH}/${completedDealId}/settlement`;
}

function getCompletedDealSettlementsQueryApiUrl() {
  return `${ACCOUNTING_API_BASE_URL}${ACCOUNTING_COMPLETED_DEALS_API_PATH}/settlements/query`;
}

function getDealsAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

export async function listDealsCustomFields(formKey) {
  const response = await fetch(getDealCustomFieldsApiUrl(formKey), { headers: getDealsAuthHeaders() });
  const payload = await readErrorPayload(response);
  if (!response.ok) throw new Error(payload?.message || 'Campi personalizzati non disponibili.');
  return Array.isArray(payload) ? payload : [];
}

export async function createDealsCustomField(definition, formKey) {
  const response = await fetch(getDealCustomFieldsApiUrl(formKey), {
    method: 'POST',
    headers: getDealsAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(definition),
  });
  const payload = await readErrorPayload(response);
  return response.ok
    ? buildMutationResult({ ok: true, data: payload })
    : buildMutationResult({ ok: false, error: 'CUSTOM_FIELD_CREATE_FAILED', message: payload?.message || 'Impossibile salvare il campo.' });
}

export async function archiveDealsCustomField(id, formKey) {
  const response = await fetch(getDealCustomFieldsApiUrl(formKey, `/${encodeURIComponent(id)}`), {
    method: 'DELETE',
    headers: getDealsAuthHeaders(),
  });
  const payload = await readErrorPayload(response);
  return response.ok
    ? buildMutationResult({ ok: true })
    : buildMutationResult({ ok: false, error: 'CUSTOM_FIELD_ARCHIVE_FAILED', message: payload?.message || 'Impossibile archiviare il campo.' });
}

function isAuthFailure(status) {
  return status === 401 || status === 403;
}

async function isModuleDisabledResponse(response) {
  if (response.status !== 403) return false;

  try {
    const payload = await response.clone().json();
    return payload?.error === 'module_disabled';
  } catch {
    return false;
  }
}

async function readErrorPayload(response) {
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

async function readCapabilityDeniedPayload(response) {
  if (response.status !== 403) return null;

  try {
    const payload = await response.clone().json();
    return payload?.error === 'capability_denied' ? payload : null;
  } catch {
    return null;
  }
}

function mapTenantDatabaseError(payload) {
  if (!TENANT_DB_ERROR_CODES.has(payload?.error)) {
    return null;
  }

  return {
    error:
      payload.error === 'tenant_database_not_ready'
        ? 'DEALS_TENANT_DATABASE_NOT_READY'
        : payload.error === 'tenant_database_metadata_missing'
          ? 'DEALS_TENANT_DATABASE_METADATA_MISSING'
          : 'DEALS_TENANT_DATABASE_UNAVAILABLE',
    message: payload?.message || 'Database tenant non disponibile per il modulo affari.',
  };
}

function normalizeOptional(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeFrontendId(value) {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
}

function findStaffByAssignmentRecipientId(staff, recipientId) {
  const matchIds = typeof idsMatch === 'function'
    ? idsMatch
    : (left, right) => String(left ?? '') !== '' && String(left) === String(right ?? '');
  return (staff || []).find((member) =>
    matchIds(member?.linkedWorkspaceUserId, recipientId)
  );
}

function buildAssignmentRecipients(form, staff, { preserveExistingSnapshots = false } = {}) {
  return (form.recipientIds || []).reduce((acc, recipientId, index) => {
    const staffMember = findStaffByAssignmentRecipientId(staff, recipientId);
    if (staffMember?.linkedWorkspaceUserId) {
      acc.recipientIds.push(String(staffMember.linkedWorkspaceUserId));
      acc.recipients.push(staffMember.name);
      return acc;
    }

    const existingRecipientName = form.recipients?.[index];
    if (preserveExistingSnapshots && existingRecipientName) {
      acc.recipientIds.push(String(recipientId));
      acc.recipients.push(existingRecipientName);
    }

    return acc;
  }, { recipientIds: [], recipients: [] });
}

function normalizeOptionalId(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function mapApiAssignmentToAssignment(assignment) {
  return {
    ...entitySelectionPayload(assignment),
    id: assignment.id,
    clientId: assignment.clientId ?? null,
    supplierId: assignment.supplierId ?? null,
    supplierName: assignment.supplierName || '',
    senderId: normalizeFrontendId(assignment.senderId),
    sender: assignment.sender || '',
    recipientIds: (assignment.recipientIds || []).map(normalizeFrontendId),
    recipients: [...(assignment.recipients || [])],
    title: assignment.title || assignment.description || '',
    description: assignment.description || '',
    type: assignment.type || 'altro',
    urgency: assignment.urgency || 'media',
    dueDate: assignment.dueDate || '',
    status: assignment.status || 'da_accettare',
    createdAt: assignment.createdAt || null,
    completedAt: assignment.completedAt || null,
    attachments: [],
    comments: [],
    ...(assignment.customFields ? { customFields: assignment.customFields } : {}),
  };
}

function buildAssignmentCreateRequest(form, staff, currentUser) {
  const clientId = normalizeOptionalId(form.clientId);
  const supplierId = normalizeOptionalId(form.supplierId);
  const supplierName = normalizeOptional(form.supplierName);
  const assignmentRecipients = buildAssignmentRecipients(form, staff);

  return {
    ...entitySelectionPayload(form),
    ...(supplierId !== null
      ? { supplierId, supplierName }
      : clientId !== null
        ? { clientId }
        : {}),
    senderId: String(currentUser.id),
    sender: currentUser.name,
    recipientIds: assignmentRecipients.recipientIds,
    recipients: assignmentRecipients.recipients,
    title: normalizeOptional(form.title),
    description: normalizeOptional(form.description),
    type: normalizeOptional(form.type) || 'altro',
    urgency: normalizeOptional(form.urgency) || 'media',
    dueDate: form.dueDate || null,
    ...(Object.prototype.hasOwnProperty.call(form, 'customFields') ? { customFields: form.customFields } : {}),
  };
}

function buildAssignmentUpdateRequest(form, staff) {
  const clientId = normalizeOptionalId(form.clientId);
  const supplierId = normalizeOptionalId(form.supplierId);
  const supplierName = normalizeOptional(form.supplierName);
  const assignmentRecipients = buildAssignmentRecipients(form, staff, { preserveExistingSnapshots: true });

  return {
    ...(supplierId !== null
      ? { supplierId, supplierName }
      : clientId !== null
        ? { clientId }
        : {}),
    recipientIds: assignmentRecipients.recipientIds,
    recipients: assignmentRecipients.recipients,
    title: normalizeOptional(form.title),
    description: normalizeOptional(form.description),
    type: normalizeOptional(form.type) || 'altro',
    urgency: normalizeOptional(form.urgency) || 'media',
    dueDate: form.dueDate || null,
    ...(Object.prototype.hasOwnProperty.call(form, 'customFields') ? { customFields: form.customFields } : {}),
  };
}

function parseMoneyValue(value) {
  const cents = parseEuroToCents(value);
  return cents === null || !Number.isFinite(cents) ? null : cents / 100;
}

function mapApiProposalToProposal(proposal) {
  const installmentDueDates = proposal.installmentDueDates || [];
  const installmentCount = proposal.installmentCount || Math.max(1, installmentDueDates.length);
  const staffId = proposal.staffId ?? proposal.assigneeStaffId ?? proposal.assignedStaffId ?? null;

  return {
    ...entitySelectionPayload(proposal),
    id: proposal.id,
    ...(proposal.clientSubmissionId ? { clientSubmissionId: proposal.clientSubmissionId } : {}),
    clientId: proposal.clientId ?? null,
    name: proposal.name || '',
    ...(proposal.description !== undefined ? { description: proposal.description || '' } : {}),
    client: proposal.client || '',
    clientContact: proposal.clientContact || '',
    clientEmail: proposal.clientEmail || '',
    clientPhone: proposal.clientPhone || '',
    tipoAffare: proposal.tipoAffare || '',
    estimatedValue:
      proposal.estimatedValue === null || proposal.estimatedValue === undefined
        ? ''
        : Number(proposal.estimatedValue),
    probability: proposal.probability === null || proposal.probability === undefined
      ? ''
      : Number(proposal.probability),
    stage: proposal.stage || '',
    assignee: proposal.assignee || '',
    staffId,
    teamCoinvolto: [...(proposal.teamCoinvolto || [])],
    notes: proposal.notes || '',
    priority: proposal.priority || 'media',
    proposalDate: proposal.proposalDate || '',
    dataInizio: proposal.proposalDate || '',
    dataPagamentoPrevista: proposal.paymentExpectedDate || '',
    origineAffare: proposal.origineAffare || '',
    dilazionamento: {
      rate: installmentCount,
      importoRata:
        proposal.installmentAmount === null || proposal.installmentAmount === undefined
          ? ''
          : Number(proposal.installmentAmount),
      ...(Array.isArray(proposal.installmentAmounts)
        ? { importiRate: getDealInstallmentCents(proposal) }
        : {}),
      scadenze: [...installmentDueDates],
    },
    commenti: [],
    inProgressDealId: proposal.inProgressDealId ?? null,
    ...(proposal.customFields ? { customFields: proposal.customFields } : {}),
  };
}

function buildProposalRequest(proposal) {
  const scadenze = (proposal.dilazionamento?.scadenze || []).filter(Boolean);

  return {
    ...(proposal.clientSubmissionId
      ? { clientSubmissionId: normalizeOptional(proposal.clientSubmissionId) }
      : {}),
    ...entitySelectionPayload(proposal),
    name: normalizeOptional(proposal.name),
    ...(normalizeOptional(proposal.description) ? { description: normalizeOptional(proposal.description) } : {}),
    ...(proposal.clientId !== null && proposal.clientId !== undefined
      ? { clientId: proposal.clientId }
      : {}),
    client: normalizeOptional(proposal.client),
    clientContact: normalizeOptional(proposal.clientContact),
    clientEmail: normalizeOptional(proposal.clientEmail),
    clientPhone: normalizeOptional(proposal.clientPhone),
    tipoAffare: normalizeOptional(proposal.tipoAffare),
    estimatedValue: parseMoneyValue(proposal.estimatedValue || proposal.value),
    ...(proposal.probability !== null && proposal.probability !== undefined && proposal.probability !== ''
      ? { probability: getDealPercentage(proposal) }
      : {}),
    stage: normalizeOptional(proposal.stage),
    assignee: normalizeOptional(proposal.assignee),
    ...(proposal.staffId !== null && proposal.staffId !== undefined && proposal.staffId !== ''
      ? { staffId: normalizeOptionalId(proposal.staffId) }
      : {}),
    teamCoinvolto: (proposal.teamCoinvolto || []).filter((member) => normalizeOptional(member) !== ''),
    notes: normalizeOptional(proposal.notes),
    priority: normalizeOptional(proposal.priority) || 'media',
    proposalDate: proposal.proposalDate || proposal.dataInizio || null,
    origineAffare: normalizeOptional(proposal.origineAffare),
    paymentExpectedDate: proposal.dataPagamentoPrevista || null,
    installmentCount: proposal.dilazionamento?.rate || Math.max(1, scadenze.length),
    installmentAmount: parseMoneyValue(proposal.dilazionamento?.importoRata),
    ...((proposal.dilazionamento?.importiRate || []).length
      ? { installmentAmounts: proposal.dilazionamento.importiRate.map((amount) => amount / 100) }
      : {}),
    installmentDueDates: scadenze,
    ...(Object.prototype.hasOwnProperty.call(proposal, 'customFields') ? { customFields: proposal.customFields } : {}),
  };
}

function hasClientId(value) {
  return value !== null && value !== undefined && value !== '';
}

function buildMissingClientMutationResult({ error, message }) {
  return buildMutationResult({
    ok: false,
    error,
    message,
  });
}

function emitInProgressPromoted(deal) {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(IN_PROGRESS_PROMOTED_EVENT, {
      detail: deal,
    })
  );
}

function emitCompletedCreated(detail) {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(COMPLETED_DEAL_CREATED_EVENT, {
      detail,
    })
  );
}

function mapApiInProgressDealToDeal(deal) {
  const installmentDueDates = deal.installmentDueDates || [];
  const installmentCount = deal.installmentCount || Math.max(1, installmentDueDates.length);
  const staffId = deal.staffId ?? deal.assigneeStaffId ?? deal.assignedStaffId ?? null;

  return {
    ...entitySelectionPayload(deal),
    id: deal.id,
    sourceProposalId: deal.sourceProposalId ?? null,
    ...(deal.accountingSyncError ? { accountingSyncError: deal.accountingSyncError } : {}),
    clientId: deal.clientId ?? null,
    name: deal.name || '',
    ...(deal.description !== undefined ? { description: deal.description || '' } : {}),
    client: deal.client || '',
    clientContact: deal.clientContact || '',
    clientEmail: deal.clientEmail || '',
    clientPhone: deal.clientPhone || '',
    tipoAffare: deal.tipoAffare || '',
    urgency: deal.urgency || 'media',
    value:
      deal.value === null || deal.value === undefined
        ? ''
        : Number(deal.value),
    probability: deal.probability === null || deal.probability === undefined
      ? ''
      : Number(deal.probability),
    stage: deal.stage || '',
    assignee: deal.assignee || '',
    staffId,
    teamCoinvolto: [...(deal.teamCoinvolto || [])],
    notes: deal.notes || '',
    origineAffare: deal.origineAffare || '',
    dataInizio: deal.startDate || '',
    dataPagamento: deal.paymentDate || '',
    dilazionamento: {
      rate: installmentCount,
      importoRata:
        deal.installmentAmount === null || deal.installmentAmount === undefined
          ? ''
          : Number(deal.installmentAmount),
      ...(Array.isArray(deal.installmentAmounts)
        ? { importiRate: getDealInstallmentCents(deal) }
        : {}),
      scadenze: [...installmentDueDates],
    },
    commenti: [],
    completedDealId: deal.completedDealId ?? null,
    ...(deal.customFields ? { customFields: deal.customFields } : {}),
  };
}

function mapApiCompletedDealToDeal(deal) {
  const installmentDueDates = deal.installmentDueDates || [];
  const installmentCount = deal.installmentCount || Math.max(1, installmentDueDates.length);
  const staffId = deal.staffId ?? deal.assigneeStaffId ?? deal.assignedStaffId ?? null;

  return {
    ...entitySelectionPayload(deal),
    id: deal.id,
    sourceInProgressDealId: deal.sourceInProgressDealId ?? null,
    clientId: deal.clientId ?? null,
    name: deal.name || '',
    ...(deal.description !== undefined ? { description: deal.description || '' } : {}),
    client: deal.client || '',
    clientContact: deal.clientContact || '',
    clientEmail: deal.clientEmail || '',
    clientPhone: deal.clientPhone || '',
    tipoAffare: deal.tipoAffare || '',
    value:
      deal.value === null || deal.value === undefined
        ? ''
        : Number(deal.value),
    probability: deal.probability === null || deal.probability === undefined
      ? ''
      : Number(deal.probability),
    completedDate: deal.completedDate || '',
    stage: deal.stage || '',
    assignee: deal.assignee || '',
    staffId,
    teamCoinvolto: [...(deal.teamCoinvolto || [])],
    notes: deal.notes || '',
    paymentStatus: deal.paymentStatus || 'da_saldare',
    origineAffare: deal.origineAffare || '',
    dataInizio: deal.startDate || '',
    dataPagamento: deal.paymentDate || '',
    dilazionamento: {
      rate: installmentCount,
      importoRata:
        deal.installmentAmount === null || deal.installmentAmount === undefined
          ? ''
          : Number(deal.installmentAmount),
      ...(Array.isArray(deal.installmentAmounts)
        ? { importiRate: getDealInstallmentCents(deal) }
        : {}),
      scadenze: [...installmentDueDates],
    },
    commenti: [],
  };
}

function mapApiCompletedDealSettlement(settlement) {
  return {
    completedDealId: settlement?.completedDealId ?? null,
    ...(Array.isArray(settlement?.outstandingDueDates) ? { outstandingDueDates: settlement.outstandingDueDates } : {}),
    ...(Array.isArray(settlement?.invoiceIds) ? { invoiceIds: settlement.invoiceIds } : {}),
    status: settlement?.status || 'da_saldare',
    totalAmount:
      settlement?.totalAmount === null || settlement?.totalAmount === undefined
        ? null
        : Number(settlement.totalAmount),
    receivedAmount:
      settlement?.receivedAmount === null || settlement?.receivedAmount === undefined
        ? null
        : Number(settlement.receivedAmount),
  };
}

function buildInProgressDealRequest(deal) {
  const scadenze = (deal.dilazionamento?.scadenze || []).filter(Boolean);

  return {
    ...entitySelectionPayload(deal),
    name: normalizeOptional(deal.name),
    ...(normalizeOptional(deal.description) ? { description: normalizeOptional(deal.description) } : {}),
    ...(deal.clientId !== null && deal.clientId !== undefined
      ? { clientId: deal.clientId }
      : {}),
    client: normalizeOptional(deal.client),
    clientContact: normalizeOptional(deal.clientContact),
    clientEmail: normalizeOptional(deal.clientEmail),
    clientPhone: normalizeOptional(deal.clientPhone),
    tipoAffare: normalizeOptional(deal.tipoAffare),
    urgency: normalizeOptional(deal.urgency) || 'media',
    value: parseMoneyValue(deal.value || deal.estimatedValue),
    ...(deal.probability !== null && deal.probability !== undefined && deal.probability !== ''
      ? { probability: getDealPercentage(deal) }
      : {}),
    stage: normalizeOptional(deal.stage),
    assignee: normalizeOptional(deal.assignee),
    ...(deal.staffId !== null && deal.staffId !== undefined && deal.staffId !== ''
      ? { staffId: normalizeOptionalId(deal.staffId) }
      : {}),
    teamCoinvolto: (deal.teamCoinvolto || []).filter((member) => normalizeOptional(member) !== ''),
    notes: normalizeOptional(deal.notes),
    origineAffare: normalizeOptional(deal.origineAffare),
    startDate: deal.dataInizio || deal.proposalDate || null,
    paymentDate: deal.dataPagamento || null,
    installmentCount: deal.dilazionamento?.rate || Math.max(1, scadenze.length),
    installmentAmount: parseMoneyValue(deal.dilazionamento?.importoRata),
    ...((deal.dilazionamento?.importiRate || []).length
      ? { installmentAmounts: deal.dilazionamento.importiRate.map((amount) => amount / 100) }
      : {}),
    installmentDueDates: scadenze,
    ...(Object.prototype.hasOwnProperty.call(deal, 'customFields') ? { customFields: deal.customFields } : {}),
  };
}

// Quarantined static UI config only. Do not add domain catalogs here.
export function getAssignmentsStaticConfig() {
  return {
    assignmentTypes: [...mock.assignmentTypes],
  };
}

export async function listAssignments() {
  try {
    const response = await fetch(getAssignmentsApiUrl(), {
      headers: getDealsAuthHeaders(),
    });

    if (!response.ok) {
      return [];
    }

    const payload = await response.json();
    return (payload || []).map(mapApiAssignmentToAssignment);
  } catch (error) {
    return [];
  }
}

export async function listAssignmentsResult() {
  try {
    const response = await fetch(getAssignmentsApiUrl(), {
      headers: getDealsAuthHeaders(),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'DEALS_MODULE_DISABLED',
        message: 'Modulo affari non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'ASSIGNMENTS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per i task.',
      });
    }

    if (!response.ok) {
      throw new Error(`ASSIGNMENTS_LIST_FAILED_${response.status}`);
    }

    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new Error('ASSIGNMENTS_LIST_INVALID_PAYLOAD');
    }

    return buildMutationResult({
      ok: true,
      data: payload.map(mapApiAssignmentToAssignment),
      error: null,
      message: '',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      data: [],
      error: 'ASSIGNMENTS_API_UNAVAILABLE',
      message: 'Catalogo task non disponibile dal backend.',
    });
  }
}

export async function createAssignment(form, staff, currentUser) {
  if (!currentUser) {
    return buildMutationResult({
      ok: false,
      error: 'SESSION_REQUIRED',
      message: 'Sessione utente non disponibile.',
    });
  }

  const formErrors = validateAssignmentForm(form);
  if (Object.keys(formErrors).length > 0) {
    return buildMutationResult({
      ok: false,
      error: 'ASSIGNMENT_VALIDATION_ERROR',
      data: {
        formErrors,
      },
      message: 'Compila correttamente i campi obbligatori.',
    });
  }

  const assignmentRecipients = buildAssignmentRecipients(form, staff);
  if (assignmentRecipients.recipientIds.length === 0) {
    return buildMutationResult({
      ok: false,
      error: 'ASSIGNMENT_VALIDATION_ERROR',
      data: {
        formErrors: {
          recipientIds: 'Seleziona almeno un destinatario con accesso gestionale.',
        },
      },
      message: 'Compila correttamente i campi obbligatori.',
    });
  }

  try {
    const response = await fetch(getAssignmentsApiUrl(), {
      method: 'POST',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(buildAssignmentCreateRequest(form, staff, currentUser)),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (response.status === 400) {
        return buildMutationResult({
          ok: false,
          error: 'ASSIGNMENT_VALIDATION_ERROR',
          data: {
            formErrors,
          },
          message: 'Compila correttamente i campi obbligatori.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'ASSIGNMENTS_API_UNAVAILABLE',
        message: 'Impossibile creare il task in questo momento.',
      });
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        assignment: mapApiAssignmentToAssignment(payload),
      },
      message: 'Task creato con successo.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'ASSIGNMENTS_API_UNAVAILABLE',
      message: 'Impossibile creare il task in questo momento.',
    });
  }
}

export async function updateAssignment(assignmentId, form, staff) {
  const formErrors = validateAssignmentForm(form);
  if (Object.keys(formErrors).length > 0) {
    return buildMutationResult({
      ok: false,
      error: 'ASSIGNMENT_VALIDATION_ERROR',
      data: {
        formErrors,
      },
      message: 'Compila correttamente i campi obbligatori.',
    });
  }

  const assignmentRecipients = buildAssignmentRecipients(form, staff, { preserveExistingSnapshots: true });
  if (assignmentRecipients.recipientIds.length === 0) {
    return buildMutationResult({
      ok: false,
      error: 'ASSIGNMENT_VALIDATION_ERROR',
      data: {
        formErrors: {
          recipientIds: 'Seleziona almeno un destinatario con accesso gestionale.',
        },
      },
      message: 'Compila correttamente i campi obbligatori.',
    });
  }

  return runAssignmentMutation(
    `/${assignmentId}`,
    {
      method: 'PUT',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(buildAssignmentUpdateRequest(form, staff)),
    },
    'Task aggiornato con successo.',
    'ASSIGNMENT_UPDATE_CONFLICT',
    'Task non aggiornabile in questo stato.'
  );
}

function mapApiAssignmentCommentToComment(comment) {
  const authorDisplayName = comment.authorDisplayName || comment.fullName || '';
  return {
    id: comment.id,
    assignmentId: comment.assignmentId,
    parentCommentId: comment.parentCommentId ?? null,
    authorUserId: comment.authorUserId,
    linkedWorkspaceUserId: comment.linkedWorkspaceUserId || '',
    authorFullName: authorDisplayName,
    authorDisplayName,
    authorName: comment.authorName || '',
    authorEmail: comment.authorEmail || comment.email || '',
    author: authorDisplayName || '',
    body: comment.body || '',
    text: comment.body || '',
    createdAt: comment.createdAt || null,
    date: comment.createdAt
      ? new Date(comment.createdAt).toLocaleString('it-IT')
      : '',
  };
}

function mapApiDealCommentToComment(comment) {
  const authorFullName = comment.authorFullName || comment.authorDisplayName || '';
  const authorName = comment.authorName || comment.authorUserId || 'Utente non risolto';
  const replies = Array.isArray(comment.replies)
    ? comment.replies.map(mapApiDealCommentToComment)
    : Array.isArray(comment.risposte)
      ? comment.risposte.map(mapApiDealCommentToComment)
      : [];
  return {
    id: comment.id,
    dealType: comment.dealType || '',
    dealId: comment.dealId ?? null,
    parentCommentId: comment.parentCommentId ?? null,
    authorUserId: comment.authorUserId,
    linkedWorkspaceUserId: comment.linkedWorkspaceUserId || '',
    authorFullName,
    authorName,
    authorEmail: comment.authorEmail || comment.email || '',
    autore: authorName,
    body: comment.body || '',
    text: comment.body || '',
    testo: comment.body || '',
    createdAt: comment.createdAt || null,
    data: comment.createdAt || null,
    date: comment.createdAt
      ? new Date(comment.createdAt).toLocaleString('it-IT')
      : '',
    replies,
    risposte: replies,
  };
}

export function resolveDealCommentsApiType(dealType) {
  switch (dealType) {
    case 'proposta':
    case 'proposal':
    case 'proposals':
      return 'proposals';
    case 'inCorso':
    case 'in-progress':
    case 'in_progress':
      return 'in-progress';
    case 'completato':
    case 'completed':
      return 'completed';
    default:
      return dealType;
  }
}

export async function listDealCommentsResult(dealType, dealId) {
  const apiDealType = resolveDealCommentsApiType(dealType);
  try {
    const response = await fetch(getDealCommentsApiUrl(apiDealType, dealId), {
      headers: getDealsAuthHeaders(),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({ ok: false, data: [], error: tenantDbError.error, message: tenantDbError.message });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'DEAL_COMMENTS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per i commenti affare.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'DEAL_NOT_FOUND',
        message: 'Affare non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`DEAL_COMMENTS_LIST_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: Array.isArray(payload) ? payload.map(mapApiDealCommentToComment) : [],
    });
  } catch {
    return buildMutationResult({
      ok: false,
      data: [],
      error: 'DEAL_COMMENTS_API_UNAVAILABLE',
      message: 'Commenti affare non disponibili dal backend.',
    });
  }
}

export async function createDealCommentResult(dealType, dealId, body, parentCommentId = null) {
  const apiDealType = resolveDealCommentsApiType(dealType);
  const normalizedBody = String(body || '').trim();
  if (!normalizedBody) {
    return buildMutationResult({
      ok: false,
      error: 'DEAL_COMMENT_VALIDATION_ERROR',
      message: 'Scrivi un commento prima di pubblicarlo.',
    });
  }

  try {
    const response = await fetch(getDealCommentsApiUrl(apiDealType, dealId), {
      method: 'POST',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        body: normalizedBody,
        ...(parentCommentId !== null && parentCommentId !== undefined ? { parentCommentId } : {}),
      }),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({ ok: false, error: tenantDbError.error, message: tenantDbError.message });
    }

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'DEAL_COMMENT_VALIDATION_ERROR',
        message: 'Scrivi un commento valido prima di pubblicarlo.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'DEAL_NOT_FOUND',
        message: 'Affare non trovato.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'DEAL_COMMENTS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per i commenti affare.',
      });
    }

    if (!response.ok) {
      throw new Error(`DEAL_COMMENT_CREATE_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: mapApiDealCommentToComment(payload),
      message: 'Commento aggiunto.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'DEAL_COMMENTS_API_UNAVAILABLE',
      message: 'Commento non salvato: backend non disponibile.',
    });
  }
}

export function createDealCommentReplyResult(dealType, dealId, parentCommentId, body) {
  return createDealCommentResult(dealType, dealId, body, parentCommentId);
}

export async function listAssignmentCommentsResult(assignmentId) {
  try {
    const response = await fetch(getAssignmentsApiUrl(`/${assignmentId}/comments`), {
      headers: getDealsAuthHeaders(),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({ ok: false, data: [], error: tenantDbError.error, message: tenantDbError.message });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'ASSIGNMENTS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per i commenti task.',
      });
    }

    if (!response.ok) {
      throw new Error(`ASSIGNMENT_COMMENTS_LIST_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: Array.isArray(payload) ? payload.map(mapApiAssignmentCommentToComment) : [],
    });
  } catch {
    return buildMutationResult({
      ok: false,
      data: [],
      error: 'ASSIGNMENT_COMMENTS_API_UNAVAILABLE',
      message: 'Commenti task non disponibili dal backend.',
    });
  }
}

export async function createAssignmentCommentResult(assignmentId, body, parentCommentId = null) {
  const normalizedBody = String(body || '').trim();
  if (!normalizedBody) {
    return buildMutationResult({
      ok: false,
      error: 'ASSIGNMENT_COMMENT_VALIDATION_ERROR',
      message: 'Scrivi un commento prima di pubblicarlo.',
    });
  }

  try {
    const response = await fetch(getAssignmentsApiUrl(`/${assignmentId}/comments`), {
      method: 'POST',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        body: normalizedBody,
        ...(parentCommentId !== null && parentCommentId !== undefined ? { parentCommentId } : {}),
      }),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({ ok: false, error: tenantDbError.error, message: tenantDbError.message });
    }

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'ASSIGNMENT_COMMENT_VALIDATION_ERROR',
        message: 'Scrivi un commento valido prima di pubblicarlo.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'ASSIGNMENT_NOT_FOUND',
        message: 'Task non trovato.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'ASSIGNMENTS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per i commenti task.',
      });
    }

    if (!response.ok) {
      throw new Error(`ASSIGNMENT_COMMENT_CREATE_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: mapApiAssignmentCommentToComment(payload),
      message: 'Commento aggiunto.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'ASSIGNMENT_COMMENTS_API_UNAVAILABLE',
      message: 'Commento non salvato: backend non disponibile.',
    });
  }
}

async function runAssignmentMutation(path, options, successMessage, conflictError, conflictMessage) {
  try {
    const response = await fetch(getAssignmentsApiUrl(path), options);

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (await isModuleDisabledResponse(response)) {
        return buildMutationResult({
          ok: false,
          error: 'DEALS_MODULE_DISABLED',
          message: 'Modulo affari non abilitato per questo workspace.',
        });
      }

      const capabilityDeniedPayload = await readCapabilityDeniedPayload(response);
      if (capabilityDeniedPayload?.capability === 'canManageDealDestructiveActions') {
        return buildMutationResult({
          ok: false,
          error: 'DEALS_DESTRUCTIVE_CAPABILITY_DENIED',
          message: capabilityDeniedPayload.message || 'Non hai i permessi per questa operazione sugli affari.',
        });
      }

      if (response.status === 404) {
        return buildMutationResult({
          ok: false,
          error: 'ASSIGNMENT_NOT_FOUND',
          message: 'Task non trovato.',
        });
      }

      if (response.status === 409) {
        return buildMutationResult({
          ok: false,
          error: conflictError,
          message: conflictMessage,
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'ASSIGNMENTS_API_UNAVAILABLE',
        message: 'Operazione non disponibile in questo momento.',
      });
    }

    const hasJsonBody = response.status !== 204;
    const payload = hasJsonBody ? await response.json() : null;

    return buildMutationResult({
      ok: true,
      data: payload ? { assignment: mapApiAssignmentToAssignment(payload) } : null,
      message: successMessage,
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'ASSIGNMENTS_API_UNAVAILABLE',
      message: 'Operazione non disponibile in questo momento.',
    });
  }
}

export async function deleteAssignment(assignmentId) {
  return runAssignmentMutation(
    `/${assignmentId}`,
    {
      method: 'DELETE',
      headers: getDealsAuthHeaders(),
    },
    'Task eliminato con successo.',
    'ASSIGNMENT_DELETE_CONFLICT',
    'Puoi eliminare solo task in stato da accettare.'
  );
}

export async function updateAssignmentClient(assignmentId, clientId) {
  return runAssignmentMutation(
    `/${assignmentId}/client`,
    {
      method: 'PATCH',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        clientId: normalizeOptionalId(clientId),
      }),
    },
    'Cliente task aggiornato con successo.',
    'ASSIGNMENT_CLIENT_UPDATE_CONFLICT',
    'Cliente task non aggiornabile in questo stato.'
  );
}

export async function acceptAssignment(assignmentId) {
  return runAssignmentMutation(
    `/${assignmentId}/accept`,
    {
      method: 'POST',
      headers: getDealsAuthHeaders(),
    },
    'Task accettato con successo.',
    'ASSIGNMENT_ACCEPT_CONFLICT',
    'Puoi accettare solo task in stato da accettare.'
  );
}

export async function rejectAssignment(assignmentId) {
  return runAssignmentMutation(
    `/${assignmentId}/reject`,
    {
      method: 'POST',
      headers: getDealsAuthHeaders(),
    },
    'Task rifiutato con successo.',
    'ASSIGNMENT_REJECT_CONFLICT',
    'Puoi rifiutare solo task in stato da accettare.'
  );
}

export async function completeAssignment(assignmentId) {
  return runAssignmentMutation(
    `/${assignmentId}/complete`,
    {
      method: 'POST',
      headers: getDealsAuthHeaders(),
    },
    'Task completato con successo.',
    'ASSIGNMENT_COMPLETE_CONFLICT',
    'Puoi completare solo task in corso.'
  );
}

async function runProposalMutation(path, options, successMessage, conflictError, conflictMessage) {
  try {
    const response = await fetch(getProposalsApiUrl(path), options);

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DEALS_MODULE_DISABLED',
        message: 'Modulo affari non abilitato per questo workspace.',
      });
    }

    const capabilityDeniedPayload = await readCapabilityDeniedPayload(response);
    if (capabilityDeniedPayload?.capability === 'canManageDealDestructiveActions') {
      return buildMutationResult({
        ok: false,
        error: 'DEALS_DESTRUCTIVE_CAPABILITY_DENIED',
        message: capabilityDeniedPayload.message || 'Non hai i permessi per questa operazione sugli affari.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'PROPOSALS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per le proposte.',
      });
    }

    if (response.status === 403) {
      return buildMutationResult({
        ok: false,
        error: 'PROPOSAL_FORBIDDEN',
        message: 'Non hai i permessi per questa operazione.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'PROPOSAL_NOT_FOUND',
        message: 'Proposta non trovata.',
      });
    }

    if (response.status === 409) {
      return buildMutationResult({
        ok: false,
        error: conflictError,
        message: conflictMessage,
      });
    }

    if (!response.ok) {
      throw new Error(`PROPOSALS_MUTATION_FAILED_${response.status}`);
    }

    const payload = response.status === 204 ? null : await response.json();
    return buildMutationResult({
      ok: true,
      data: payload ? { proposal: mapApiProposalToProposal(payload) } : null,
      message: successMessage,
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'PROPOSALS_API_UNAVAILABLE',
      message: 'Operazione non disponibile in questo momento.',
    });
  }
}

async function runInProgressMutation(path, options, successMessage, conflictError, conflictMessage) {
  try {
    const response = await fetch(getInProgressApiUrl(path), options);

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DEALS_MODULE_DISABLED',
        message: 'Modulo affari non abilitato per questo workspace.',
      });
    }

    const capabilityDeniedPayload = await readCapabilityDeniedPayload(response);
    if (capabilityDeniedPayload?.capability === 'canManageDealDestructiveActions') {
      return buildMutationResult({
        ok: false,
        error: 'DEALS_DESTRUCTIVE_CAPABILITY_DENIED',
        message: capabilityDeniedPayload.message || 'Non hai i permessi per questa operazione sugli affari.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'IN_PROGRESS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per gli affari in corso.',
      });
    }

    if (response.status === 403) {
      return buildMutationResult({
        ok: false,
        error: 'IN_PROGRESS_FORBIDDEN',
        message: 'Non hai i permessi per questa operazione.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'IN_PROGRESS_NOT_FOUND',
        message: 'Affare in corso non trovato.',
      });
    }

    if (response.status === 409) {
      return buildMutationResult({
        ok: false,
        error: conflictError,
        message: conflictMessage,
      });
    }

    if (!response.ok) {
      throw new Error(`IN_PROGRESS_MUTATION_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: { deal: mapApiInProgressDealToDeal(payload) },
      message: successMessage,
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'IN_PROGRESS_API_UNAVAILABLE',
      message: 'Operazione non disponibile in questo momento.',
    });
  }
}

// Quarantined static UI config only. Do not add domain catalogs here.
export function getDealProposalsStaticConfig() {
  return {
    proposalStages: [...mock.proposalStages],
  };
}

export async function listProposals() {
  try {
    const response = await fetch(getProposalsApiUrl(), {
      headers: getDealsAuthHeaders(),
    });

    if (!response.ok) {
      return [];
    }

    const payload = await response.json();
    return (payload || []).map(mapApiProposalToProposal);
  } catch (error) {
    return [];
  }
}

export async function deleteProposal(proposalId) {
  return runProposalMutation(
    `/${proposalId}`,
    {
      method: 'DELETE',
      headers: getDealsAuthHeaders(),
    },
    'Proposta eliminata con successo.',
    'PROPOSAL_DELETE_CONFLICT',
    'Puoi eliminare solo proposte non promosse e ancora in stato iniziale.'
  );
}

export async function listProposalsResult() {
  try {
    const response = await fetch(getProposalsApiUrl(), {
      headers: getDealsAuthHeaders(),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'DEALS_MODULE_DISABLED',
        message: 'Modulo affari non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'PROPOSALS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per le proposte.',
      });
    }

    if (!response.ok) {
      throw new Error(`PROPOSALS_LIST_FAILED_${response.status}`);
    }

    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new Error('PROPOSALS_LIST_INVALID_PAYLOAD');
    }

    return buildMutationResult({
      ok: true,
      data: payload.map(mapApiProposalToProposal),
      error: null,
      message: '',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      data: [],
      error: 'PROPOSALS_API_UNAVAILABLE',
      message: 'Catalogo proposte non disponibile dal backend.',
    });
  }
}

export async function createProposal(proposal) {
  if (!hasClientId(proposal?.clientId)) {
    return buildMissingClientMutationResult({
      error: 'PROPOSAL_CLIENT_REQUIRED',
      message: 'Seleziona un cliente reale dal catalogo prima di creare la proposta.',
    });
  }
  if (!normalizeOptional(proposal?.clientSubmissionId)) {
    return buildMissingClientMutationResult({
      error: 'PROPOSAL_SUBMISSION_ID_REQUIRED',
      message: 'Riapri il form per avviare una nuova creazione proposta.',
    });
  }

  try {
    const response = await fetch(getProposalsApiUrl(), {
      method: 'POST',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(buildProposalRequest(proposal)),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (response.status === 400) {
        return buildMutationResult({
          ok: false,
          error: 'PROPOSAL_VALIDATION_ERROR',
          message: 'Compila correttamente i campi obbligatori della proposta.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'PROPOSALS_API_UNAVAILABLE',
        message: 'Impossibile creare la proposta in questo momento.',
      });
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        proposal: mapApiProposalToProposal(payload),
      },
      message: 'Proposta creata con successo.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'PROPOSALS_API_UNAVAILABLE',
      message: 'Impossibile creare la proposta in questo momento.',
    });
  }
}

export async function saveProposal(updatedProposal) {
  if (!hasClientId(updatedProposal?.clientId)) {
    return buildMissingClientMutationResult({
      error: 'PROPOSAL_CLIENT_REQUIRED',
      message: 'Seleziona un cliente reale dal catalogo prima di salvare la proposta.',
    });
  }

  try {
    const response = await fetch(getProposalsApiUrl(`/${updatedProposal.id}`), {
      method: 'PUT',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(buildProposalRequest(updatedProposal)),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (response.status === 404) {
        return buildMutationResult({
          ok: false,
          error: 'PROPOSAL_NOT_FOUND',
          message: 'Proposta non trovata.',
        });
      }

      if (response.status === 400) {
        return buildMutationResult({
          ok: false,
          error: 'PROPOSAL_VALIDATION_ERROR',
          message: 'Compila correttamente i campi obbligatori della proposta.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'PROPOSALS_API_UNAVAILABLE',
        message: 'Impossibile aggiornare la proposta in questo momento.',
      });
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        proposal: mapApiProposalToProposal(payload),
      },
      message: 'Proposta aggiornata con successo.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'PROPOSALS_API_UNAVAILABLE',
      message: 'Impossibile aggiornare la proposta in questo momento.',
    });
  }
}

export async function updateProposalStage(proposalId, newStage) {
  try {
    const response = await fetch(getProposalsApiUrl(`/${proposalId}/stage`), {
      method: 'POST',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        stage: newStage,
      }),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (response.status === 404) {
        return buildMutationResult({
          ok: false,
          error: 'PROPOSAL_NOT_FOUND',
          message: 'Proposta non trovata.',
        });
      }

      if (response.status === 400) {
        return buildMutationResult({
          ok: false,
          error: 'PROPOSAL_STAGE_INVALID',
          message: 'La fase proposta non è valida.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'PROPOSALS_API_UNAVAILABLE',
        message: 'Impossibile aggiornare la fase proposta in questo momento.',
      });
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        proposal: mapApiProposalToProposal(payload),
      },
      message: 'Fase proposta aggiornata.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'PROPOSALS_API_UNAVAILABLE',
      message: 'Impossibile aggiornare la fase proposta in questo momento.',
    });
  }
}

export async function promoteProposalToInProgress(proposalId) {
  try {
    const response = await fetch(getProposalsApiUrl(`/${proposalId}/promote-to-in-progress`), {
      method: 'POST',
      headers: getDealsAuthHeaders(),
    });

    if (!response.ok) {
      const errorPayload = await readErrorPayload(response);
      const tenantDbError = mapTenantDatabaseError(errorPayload);
      if (tenantDbError) {
        return buildMutationResult({
          ok: false,
          error: tenantDbError.error,
          message: tenantDbError.message,
        });
      }

      if (response.status === 404) {
        return buildMutationResult({
          ok: false,
          error: 'PROPOSAL_NOT_FOUND',
          message: 'Proposta non trovata.',
        });
      }

      if (response.status === 409 && errorPayload?.error === 'proposal_already_promoted') {
        return buildMutationResult({
          ok: false,
          error: 'PROPOSAL_ALREADY_PROMOTED',
          message: 'La proposta è già stata promossa a In corso.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'PROPOSALS_API_UNAVAILABLE',
        message: 'Impossibile promuovere la proposta in questo momento.',
      });
    }

    const payload = await response.json();
    const proposal = mapApiProposalToProposal(payload.proposal);
    const inProgressDeal = mapApiInProgressDealToDeal(payload.inProgressDeal);

    emitInProgressPromoted(inProgressDeal);

    return buildMutationResult({
      ok: true,
      data: {
        proposal,
        inProgressDeal,
      },
      message: 'Proposta promossa a In corso con successo.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'PROPOSALS_API_UNAVAILABLE',
      message: 'Impossibile promuovere la proposta in questo momento.',
    });
  }
}

export function subscribeToInProgressPromoted(callback) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  function handleEvent(event) {
    callback?.(event.detail);
  }

  window.addEventListener(IN_PROGRESS_PROMOTED_EVENT, handleEvent);
  return () => window.removeEventListener(IN_PROGRESS_PROMOTED_EVENT, handleEvent);
}

export function subscribeToCompletedCreated(callback) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  function handleEvent(event) {
    callback?.(event.detail);
  }

  window.addEventListener(COMPLETED_DEAL_CREATED_EVENT, handleEvent);
  return () => window.removeEventListener(COMPLETED_DEAL_CREATED_EVENT, handleEvent);
}

// Quarantined static UI config only. Do not add domain catalogs here.
export function getDealsInProgressStaticConfig() {
  return {
    dealsInProgress: [],
    dealStages: [...mock.dealStages],
  };
}

export async function listInProgressDeals() {
  try {
    const response = await fetch(getInProgressApiUrl(), {
      headers: getDealsAuthHeaders(),
    });

    if (!response.ok) {
      return [];
    }

    const payload = await response.json();
    return (payload || []).map(mapApiInProgressDealToDeal);
  } catch (error) {
    return [];
  }
}

export async function cancelInProgressDeal(dealId) {
  return runInProgressMutation(
    `/${dealId}/cancel`,
    {
      method: 'POST',
      headers: getDealsAuthHeaders(),
    },
    'Affare in corso annullato con successo.',
    'IN_PROGRESS_CANCEL_CONFLICT',
    'Puoi annullare solo affari non completati e non gia annullati.'
  );
}

export async function returnInProgressDealToProposal(dealId) {
  try {
    const response = await fetch(getInProgressApiUrl(`/${dealId}/return-to-proposal`), {
      method: 'POST',
      headers: getDealsAuthHeaders(),
    });
    const payload = await readErrorPayload(response);
    if (response.status === 403 && payload?.error === 'capability_denied') {
      return buildMutationResult({ ok: false, error: 'DEALS_DESTRUCTIVE_CAPABILITY_DENIED', message: payload.message });
    }
    if (response.status === 404) {
      return buildMutationResult({ ok: false, error: 'IN_PROGRESS_NOT_FOUND', message: 'Affare in corso non trovato.' });
    }
    if (response.status === 409) {
      return buildMutationResult({ ok: false, error: payload?.error || 'IN_PROGRESS_RETURN_CONFLICT', message: payload?.message || 'Questo affare non può essere riportato alle proposte.' });
    }
    if (!response.ok) throw new Error(`IN_PROGRESS_RETURN_FAILED_${response.status}`);
    return buildMutationResult({ ok: true, data: await response.json(), message: 'Affare riportato alle proposte.' });
  } catch {
    return buildMutationResult({ ok: false, error: 'IN_PROGRESS_API_UNAVAILABLE', message: 'Operazione non disponibile in questo momento.' });
  }
}

export async function listInProgressDealsResult() {
  try {
    const response = await fetch(getInProgressApiUrl(), {
      headers: getDealsAuthHeaders(),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'DEALS_MODULE_DISABLED',
        message: 'Modulo affari non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'IN_PROGRESS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per gli affari in corso.',
      });
    }

    if (!response.ok) {
      throw new Error(`IN_PROGRESS_LIST_FAILED_${response.status}`);
    }

    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new Error('IN_PROGRESS_LIST_INVALID_PAYLOAD');
    }

    return buildMutationResult({
      ok: true,
      data: payload.map(mapApiInProgressDealToDeal),
      error: null,
      message: '',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      data: [],
      error: 'IN_PROGRESS_API_UNAVAILABLE',
      message: 'Catalogo affari in corso non disponibile dal backend.',
    });
  }
}

export async function createInProgressDeal(deal) {
  if (!hasClientId(deal?.clientId)) {
    return buildMissingClientMutationResult({
      error: 'IN_PROGRESS_CLIENT_REQUIRED',
      message: 'Seleziona un cliente reale dal catalogo prima di creare l\'affare.',
    });
  }

  try {
    const response = await fetch(getInProgressApiUrl(), {
      method: 'POST',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(buildInProgressDealRequest(deal)),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (response.status === 400) {
        return buildMutationResult({
          ok: false,
          error: 'IN_PROGRESS_VALIDATION_ERROR',
          message: 'Compila correttamente i campi obbligatori dell\'affare.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'IN_PROGRESS_API_UNAVAILABLE',
        message: 'Impossibile creare l\'affare in questo momento.',
      });
    }

    const payload = await response.json();

    return buildMutationResult({
      ok: true,
      data: {
        deal: mapApiInProgressDealToDeal(payload),
      },
      message: payload.accountingSyncError || 'Affare creato con successo.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'IN_PROGRESS_API_UNAVAILABLE',
      message: 'Impossibile creare l\'affare in questo momento.',
    });
  }
}

export async function saveInProgressDeal(updatedDeal) {
  if (!hasClientId(updatedDeal?.clientId)) {
    return buildMissingClientMutationResult({
      error: 'IN_PROGRESS_CLIENT_REQUIRED',
      message: 'Seleziona un cliente reale dal catalogo prima di salvare l\'affare.',
    });
  }

  try {
    const response = await fetch(getInProgressApiUrl(`/${updatedDeal.id}`), {
      method: 'PUT',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(buildInProgressDealRequest(updatedDeal)),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (response.status === 404) {
        return buildMutationResult({
          ok: false,
          error: 'DEAL_NOT_FOUND',
          message: 'Affare non trovato.',
        });
      }

      if (response.status === 400) {
        return buildMutationResult({
          ok: false,
          error: 'IN_PROGRESS_VALIDATION_ERROR',
          message: 'Compila correttamente i campi obbligatori dell\'affare.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'IN_PROGRESS_API_UNAVAILABLE',
        message: 'Impossibile aggiornare l\'affare in questo momento.',
      });
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        deal: mapApiInProgressDealToDeal(payload),
      },
      message: 'Affare aggiornato con successo.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'IN_PROGRESS_API_UNAVAILABLE',
      message: 'Impossibile aggiornare l\'affare in questo momento.',
    });
  }
}

export async function updateInProgressDealStage(dealId, newStage) {
  try {
    const response = await fetch(getInProgressApiUrl(`/${dealId}/stage`), {
      method: 'POST',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        stage: newStage,
      }),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (response.status === 404) {
        return buildMutationResult({
          ok: false,
          error: 'DEAL_NOT_FOUND',
          message: 'Affare non trovato.',
        });
      }

      if (response.status === 400) {
        return buildMutationResult({
          ok: false,
          error: 'IN_PROGRESS_STAGE_INVALID',
          message: 'La fase affare non è valida.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'IN_PROGRESS_API_UNAVAILABLE',
        message: 'Impossibile aggiornare la fase affare in questo momento.',
      });
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        deal: mapApiInProgressDealToDeal(payload),
      },
      message: 'Fase affare aggiornata.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'IN_PROGRESS_API_UNAVAILABLE',
      message: 'Impossibile aggiornare la fase affare in questo momento.',
    });
  }
}

export async function completeInProgressDeal(dealId, paymentStatus) {
  try {
    const response = await fetch(getInProgressApiUrl(`/${dealId}/complete`), {
      method: 'POST',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        paymentStatus,
      }),
    });

    const errorPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(errorPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (response.status === 404) {
        return buildMutationResult({
          ok: false,
          error: 'DEAL_NOT_FOUND',
          message: 'Affare non trovato.',
        });
      }

      if (response.status === 409) {
        if (errorPayload?.error === 'deal_already_completed') {
          return buildMutationResult({
            ok: false,
            error: 'DEAL_ALREADY_COMPLETED',
            message: errorPayload.message || 'L\'affare è già stato completato.',
          });
        }

        if (errorPayload?.error === 'deal_cancelled') {
          return buildMutationResult({
            ok: false,
            error: 'DEAL_CANCELLED',
            message: errorPayload.message || 'L\'affare è stato annullato e non può essere completato.',
          });
        }

        return buildMutationResult({
          ok: false,
          error: 'DEAL_COMPLETION_CONFLICT',
          message: errorPayload?.message || 'L\'affare non può essere completato nello stato corrente.',
        });
      }

      if (response.status === 400) {
        return buildMutationResult({
          ok: false,
          error: 'DEAL_COMPLETION_STATUS_REQUIRED',
          message: 'Seleziona lo stato settlement prima di completare l\'affare.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'IN_PROGRESS_API_UNAVAILABLE',
        message: 'Impossibile completare l\'affare in questo momento.',
      });
    }

    const payload = await response.json();
    const inProgressDeal = mapApiInProgressDealToDeal(payload.inProgressDeal);
    const completedDeal = mapApiCompletedDealToDeal(payload.completedDeal);

    emitCompletedCreated({
      completedDeal,
      sourceInProgressDealId: inProgressDeal.id,
    });

    return buildMutationResult({
      ok: true,
      data: {
        inProgressDeal,
        completedDeal,
        ...(payload.inProgressDeal?.accountingSyncError ? { accountingSyncError: payload.inProgressDeal.accountingSyncError } : {}),
      },
      message: payload.inProgressDeal?.accountingSyncError || 'Affare completato con successo.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'IN_PROGRESS_API_UNAVAILABLE',
      message: 'Impossibile completare l\'affare in questo momento.',
    });
  }
}

export async function updateCompletedDealStage(dealId, newStage) {
  try {
    const response = await fetch(getCompletedApiUrl(`/${dealId}/stage`), {
      method: 'POST',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        stage: newStage,
      }),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (response.status === 404) {
        return buildMutationResult({
          ok: false,
          error: 'COMPLETED_DEAL_NOT_FOUND',
          message: 'Affare completato non trovato.',
        });
      }

      if (response.status === 400) {
        return buildMutationResult({
          ok: false,
          error: 'COMPLETED_STAGE_INVALID',
          message: 'La fase affare completato non è valida.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'COMPLETED_DEALS_API_UNAVAILABLE',
        message: 'Impossibile aggiornare la fase affare completato in questo momento.',
      });
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        deal: mapApiCompletedDealToDeal(payload),
      },
      message: 'Fase affare completato aggiornata.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'COMPLETED_DEALS_API_UNAVAILABLE',
      message: 'Impossibile aggiornare la fase affare completato in questo momento.',
    });
  }
}

export async function updateCompletedDealStaff(dealId, staffAssignment) {
  try {
    const response = await fetch(getCompletedApiUrl(`/${dealId}/staff`), {
      method: 'PUT',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        ...entitySelectionPayload(staffAssignment),
        staffId: normalizeOptionalId(staffAssignment?.staffId),
        assignee: normalizeOptional(staffAssignment?.assignee),
      }),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DEALS_MODULE_DISABLED',
        message: 'Modulo affari non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'COMPLETED_DEALS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per gli affari completati.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'COMPLETED_DEAL_NOT_FOUND',
        message: 'Affare completato non trovato.',
      });
    }

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'COMPLETED_STAFF_INVALID',
        message: 'Seleziona un responsabile staff valido.',
      });
    }

    if (!response.ok) {
      throw new Error(`COMPLETED_DEALS_STAFF_UPDATE_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        deal: mapApiCompletedDealToDeal(payload),
      },
      message: 'Responsabile affare completato aggiornato.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'COMPLETED_DEALS_API_UNAVAILABLE',
      message: 'Impossibile aggiornare il responsabile affare completato in questo momento.',
    });
  }
}

export async function deleteCompletedDeal(dealId) {
  try {
    const response = await fetch(getCompletedApiUrl(`/${dealId}`), {
      method: 'DELETE',
      headers: getDealsAuthHeaders(),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DEALS_MODULE_DISABLED',
        message: 'Modulo affari non abilitato per questo workspace.',
      });
    }

    const capabilityDeniedPayload = await readCapabilityDeniedPayload(response);
    if (capabilityDeniedPayload?.capability === 'canManageDealDestructiveActions') {
      return buildMutationResult({
        ok: false,
        error: 'DEALS_DESTRUCTIVE_CAPABILITY_DENIED',
        message: capabilityDeniedPayload.message || 'Non hai i permessi per eliminare affari completati.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'COMPLETED_DEALS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per gli affari completati.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'COMPLETED_DEAL_NOT_FOUND',
        message: 'Affare completato non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`COMPLETED_DEALS_DELETE_FAILED_${response.status}`);
    }

    return buildMutationResult({
      ok: true,
      message: 'Affare completato eliminato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'COMPLETED_DEALS_API_UNAVAILABLE',
      message: 'Impossibile eliminare l\'affare completato in questo momento.',
    });
  }
}

// Quarantined static UI config only. Do not add domain catalogs here.
export function getCompletedDealsStaticConfig() {
  return {
    dealsCompleted: [],
    dealStages: [...mock.dealStages],
  };
}

export async function listCompletedDeals() {
  try {
    const response = await fetch(getCompletedApiUrl(), {
      headers: getDealsAuthHeaders(),
    });

    if (!response.ok) {
      return [];
    }

    const payload = await response.json();
    return (payload || []).map(mapApiCompletedDealToDeal);
  } catch (error) {
    return [];
  }
}

export async function listCompletedDealsResult() {
  try {
    const response = await fetch(getCompletedApiUrl(), {
      headers: getDealsAuthHeaders(),
    });

    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'DEALS_MODULE_DISABLED',
        message: 'Modulo affari non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        data: [],
        error: 'COMPLETED_DEALS_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per gli affari completati.',
      });
    }

    if (!response.ok) {
      throw new Error(`COMPLETED_DEALS_LIST_FAILED_${response.status}`);
    }

    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new Error('COMPLETED_DEALS_LIST_INVALID_PAYLOAD');
    }

    return buildMutationResult({
      ok: true,
      data: payload.map(mapApiCompletedDealToDeal),
      error: null,
      message: '',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      data: [],
      error: 'COMPLETED_DEALS_API_UNAVAILABLE',
      message: 'Catalogo affari completati non disponibile dal backend.',
    });
  }
}

export async function getCompletedDealSettlement(completedDealId) {
  try {
    const response = await fetch(getCompletedDealSettlementApiUrl(completedDealId), {
      headers: getDealsAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        return buildMutationResult({
          ok: false,
          error: 'SETTLEMENT_NOT_FOUND',
          message: 'Nessun pagamento collegato a questo affare in accounting.',
        });
      }

      if (isAuthFailure(response.status)) {
        return buildMutationResult({
          ok: false,
          error: 'SETTLEMENT_AUTH_REQUIRED',
          message: 'Sessione non autorizzata per leggere il settlement.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'SETTLEMENT_API_UNAVAILABLE',
        message: 'Impossibile leggere il settlement reale in questo momento.',
      });
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: mapApiCompletedDealSettlement(payload),
      message: '',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'SETTLEMENT_API_UNAVAILABLE',
      message: 'Impossibile leggere il settlement reale in questo momento.',
    });
  }
}

export async function getCompletedDealSettlements(completedDealIds) {
  const normalizedIds = Array.from(
    new Set((completedDealIds || []).filter((value) => value !== null && value !== undefined))
  );

  if (normalizedIds.length === 0) {
    return buildMutationResult({
      ok: true,
      data: [],
      message: '',
    });
  }

  try {
    const response = await fetch(getCompletedDealSettlementsQueryApiUrl(), {
      method: 'POST',
      headers: getDealsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        completedDealIds: normalizedIds,
      }),
    });

    if (!response.ok) {
      if (isAuthFailure(response.status)) {
        return buildMutationResult({
          ok: false,
          error: 'SETTLEMENT_BATCH_AUTH_REQUIRED',
          message: 'Sessione non autorizzata per leggere i settlement.',
        });
      }

      return buildMutationResult({
        ok: false,
        error: 'SETTLEMENT_BATCH_API_UNAVAILABLE',
        message: 'Impossibile leggere i settlement reali in questo momento.',
      });
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: Array.isArray(payload) ? payload.map(mapApiCompletedDealSettlement) : [],
      message: '',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: 'SETTLEMENT_BATCH_API_UNAVAILABLE',
      message: 'Impossibile leggere i settlement reali in questo momento.',
    });
  }
}

export { mapApiCompletedDealToDeal, mapApiInProgressDealToDeal, mapApiProposalToProposal };
